import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSync, faXmarkCircle, faPencil, faXmark } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Badge } from '@shared/components/ui/badge';
import { RadioSelect } from '@shared/components/ui/RadioSelect';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { Input } from '@shared/components/ui/input';
import { Separator, Switch } from '@shared/components/ui/shadcn';
import { ShopsModel, CHANNELS_MINIMAL_SETTINGS } from '../../models/Shops';
import { ShopAvatar } from '../display/ShopAvatar';
import { AddPaymentMethodButton, ImportFiltersSummary } from '../interaction';
import { usePaymentMethods, useSettingsManager } from '../../hooks';

export interface ShopSettingsModalRef {
  open: (shop: ShopsModel) => void;
}

export const ShopSettingsModal = forwardRef<ShopSettingsModalRef>((_, ref) => {
  const { t } = useTranslation('shopsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [shop, setShop] = useState<ShopsModel | null>(null);
  const [approveDefault, setApproveDefault] = useState('');
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState('');
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  // Cargar medios de pago
  const { paymentMethods, loading: paymentMethodsLoading } = usePaymentMethods();

  // Hook para manejar métodos de pago (consistencia con modal móvil)
  const {
    updatePaymentMethods,
    getPaymentMethodsByCategory,
    addPaymentMethod,
    removePaymentMethod,
    saveSettings,
    isSaving,
  } = useSettingsManager();

  // 🎯 Funciones para manejar edición del nombre
  const handleStartEditName = () => {
    setEditedName(shop?.name || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setEditedName('');
    setIsEditingName(false);
  };

  const handleSaveName = (onSuccess?: () => void) => {
    if (shop && editedName.trim() !== shop.name) {
      const shopToUse = { ...shop, name: editedName.trim() };
      saveSettings(
        shopToUse,
        {
          approveDefault,
          defaultPaymentMethod,
          trackingEnabled,
        },
        () => {
          setShop(shopToUse);
          setIsEditingName(false);
          onSuccess?.();
        },
      );
    } else {
      setIsEditingName(false);
    }
  };

  const handleMinimalSave = () => {
    const shopToUse = isEditingName ? { ...shop!, name: editedName.trim() } : shop!;
    saveSettings(
      shopToUse,
      {
        approveDefault,
        defaultPaymentMethod,
        trackingEnabled,
      },
      () => modalRef.current?.closeModal(),
    );
  };

  // Asegurar que el modal esté montado antes de intentar abrirlo
  useEffect(() => {
    // El modal se inicializa automáticamente
  }, []);

  useImperativeHandle(ref, () => ({
    open: (shop: ShopsModel) => {
      setShop(shop);
      // Inicializar con el valor de approval_type del objeto shop
      setApproveDefault(shop.approval_type.toString());
      // Inicializar con el valor de default_payment_method_id del objeto shop
      setDefaultPaymentMethod(shop.default_payment_method_id?.toString() || '');
      // Inicializar el switch con el valor de fulfillment
      setTrackingEnabled(shop.fulfillment !== null);
      // Inicializar los métodos de pago vinculados
      updatePaymentMethods(shop.linked_payment_methods || []);

      // Pequeño retraso para asegurar que el modal esté montado
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.openModal();
        }
      }, 0);
    },
  }));

  if (!shop) return null;

  const isMinimalSettings = CHANNELS_MINIMAL_SETTINGS.includes(shop.channel_id);

  return (
    <ModalCenter
      ref={modalRef}
      title={t('modals.settings.title')}
      type="default"
      width={isMinimalSettings ? 'md' : 'xxl'}
      className="bg-card-bg !outline-0"
      classNameBody="max-h-[75vh]"
      footer={
        <div className="flex justify-end gap-2 w-full">
          <Button
            variant="outline"
            onClick={() => modalRef.current?.closeModal()}
            data-testid="cancel-settings-button-desktop"
          >
            {t('modals.settings.buttons.cancel')}
          </Button>
          {isMinimalSettings ? (
            <Button
              variant="primary"
              onClick={handleMinimalSave}
              disabled={isSaving}
              className="w-fit"
              data-testid="save-settings-button-desktop"
            >
              {isSaving ? t('common.loading') : t('modals.settings.buttons.save')}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => {
                if (shop) {
                  const shopToUse = isEditingName ? { ...shop, name: editedName.trim() } : shop;

                  saveSettings(
                    shopToUse,
                    {
                      approveDefault,
                      defaultPaymentMethod,
                      trackingEnabled,
                    },
                    () => modalRef.current?.closeModal(),
                  );
                }
              }}
              disabled={isSaving}
              className="w-fit"
              data-testid="save-settings-button-desktop"
            >
              {isSaving ? t('common.loading') : t('modals.settings.buttons.save')}
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-8 pb-4">
        <Separator />
        {/* General Section - siempre visible */}
        <section>
          {!isMinimalSettings && (
            <div className="flex flex-col md:flex-row justify-between items-start gap-6 mb-6">
              <div className="w-full md:w-[50%] flex flex-col gap-5">
                <h2 className="text-lg font-medium mb-4">{t('modals.settings.general.title')}</h2>
              </div>
              <div className="w-full md:w-[50%] flex flex-col gap-5">
                <div className="flex items-center gap-3 text-sm ml-auto">
                  <label>{t('modals.settings.general.approveDefault.label')}</label>
                  <InfoTooltip text={t('modals.settings.general.approveDefaultTooltip')} />
                  <RadioSelect
                    options={[
                      {
                        value: '0',
                        label: t('modals.settings.general.approveDefault.none.label'),
                        description: t('modals.settings.general.approveDefault.none.description'),
                      },
                      {
                        value: '1',
                        label: t('modals.settings.general.approveDefault.all.label'),
                        description: t('modals.settings.general.approveDefault.all.description'),
                      },
                      {
                        value: '2',
                        label: t('modals.settings.general.approveDefault.paid.label'),
                        description: t('modals.settings.general.approveDefault.paid.description'),
                      },
                    ]}
                    value={approveDefault}
                    setValue={setApproveDefault}
                    placeholder={t('modals.settings.general.selectPlaceholder')}
                    className="w-full"
                    dropdownWidth="md"
                    dataTestId="approve-default-select"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <ShopAvatar shop={shop} size="lg" />
              <div>
                {isEditingName ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveName();
                        }
                      }}
                      className="text-xl font-medium h-[34px]"
                      data-testid="shop-name-input"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelEditName}
                      className="!p-2"
                      data-testid="cancel-name-button"
                    >
                      <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <h3 className="text-xl font-medium">{shop.name}</h3>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleStartEditName}
                      className="!p-0 border-0 bg-transparent"
                      data-testid="edit-name-button"
                    >
                      <FontAwesomeIcon icon={faPencil} className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                <div className="flex gap-2 mt-4">
                  <Badge
                    variant="outline"
                    className={`bg-card-border border-0 text-xs py-1 ${
                      shop.status > 0 ? 'light:bg-bg-success' : 'light:bg-bg-error'
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={shop.status > 0 ? faCheckCircle : faXmarkCircle}
                      className={`mr-2 ${shop.status > 0 ? 'text-success dark:text-bright-success' : 'text-error'}`}
                    />
                    {shop.status > 0 ? t('modals.settings.general.active') : t('modals.settings.general.inactive')}
                  </Badge>
                  <Badge variant="outline" className="bg-card-border border-0 text-xs py-1 light:bg-bg-success">
                    <FontAwesomeIcon icon={faSync} className="text-success dark:text-bright-success mr-2" />
                    {t('modals.settings.general.synced')}
                  </Badge>
                </div>
              </div>
            </div>
            {!isMinimalSettings && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 justify-end text-sm">
                  <Switch id="tracking-switch" checked={trackingEnabled} onCheckedChange={setTrackingEnabled} />
                  <label htmlFor="tracking-switch">{t('modals.settings.general.uploadTracking')}</label>
                  <InfoTooltip text={t('modals.settings.general.uploadTrackingTooltip')} position="bottom-right" />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Payments Section - oculta para canales mínimos */}
        {!isMinimalSettings && (
          <section>
            <h2 className="text-lg font-medium mb-4">{t('modals.settings.payments.title')}</h2>
            <div className="flex flex-col md:flex-row justify-start items-start gap-8">
              <div className="w-full md:w-[33%]">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <h2 className="font-medium">{t('modals.settings.payments.defaultPaymentMethod')}</h2>
                  <InfoTooltip text={t('modals.settings.payments.defaultPaymentMethodTooltip')} maxWidth="md" />
                </div>
                <RadioSelect
                  options={paymentMethods.map((method: any) => ({
                    value: method.id,
                    label: method.name,
                  }))}
                  value={defaultPaymentMethod}
                  setValue={setDefaultPaymentMethod}
                  placeholder={paymentMethodsLoading ? 'Cargando...' : t('modals.settings.general.selectPlaceholder')}
                  dropdownWidth="sm"
                  dataTestId="default-payment-method-select"
                  disabled={paymentMethodsLoading}
                />
              </div>
              <div className="w-full md:w-[30%]">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="font-medium">{t('modals.settings.payments.linkPaymentMethods')}</h2>
                  <InfoTooltip text={t('modals.settings.payments.linkPaymentMethodsTooltip')} maxWidth="md" />
                </div>
                {(() => {
                  const categories = getPaymentMethodsByCategory();
                  return (
                    <div className="space-y-4">
                      {/* Pedido pagado */}
                      <div>
                        <label className="flex items-center gap-2 mb-2 text-sm justify-between">
                          {t('modals.settings.payments.categories.paidOrder')}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <AddPaymentMethodButton
                            linkedPaymentMethods={categories.paidOrder}
                            paymentMethodId={1}
                            onAddMethod={addPaymentMethod}
                            onRemoveMethod={removePaymentMethod}
                          />
                        </div>
                      </div>

                      {/* Transferencia bancaria */}
                      <div>
                        <label className="flex items-center gap-2 mb-2 text-sm justify-between">
                          {t('modals.settings.payments.categories.bankTransfer')}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <AddPaymentMethodButton
                            linkedPaymentMethods={categories.bankTransfer}
                            paymentMethodId={2}
                            onAddMethod={addPaymentMethod}
                            onRemoveMethod={removePaymentMethod}
                          />
                        </div>
                      </div>

                      {/* Contra reembolso */}
                      <div>
                        <label className="flex items-center gap-2 mb-2 text-sm justify-between">
                          {t('modals.settings.payments.categories.cashOnDelivery')}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <AddPaymentMethodButton
                            linkedPaymentMethods={categories.cashOnDelivery}
                            paymentMethodId={3}
                            onAddMethod={addPaymentMethod}
                            onRemoveMethod={removePaymentMethod}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="w-full md:w-[35%] min-h-[150px]">
                <ImportFiltersSummary
                  shopId={shop.id}
                  initialFilters={shop.import_filters}
                  onFiltersUpdated={(newFilters) => {
                    setShop((prev) => (prev ? { ...prev, import_filters: newFilters } : null));
                  }}
                />
              </div>
            </div>
          </section>
        )}
      </div>
    </ModalCenter>
  );
});

ShopSettingsModal.displayName = 'ShopSettingsModal';
